import catIcon from '@common/_assets/images/icon-cat.svg';
import dogIcon from '@common/_assets/images/icon-dog.svg';
import { YesNoConfirmModal } from "@common/components/modals";
import { useApp } from '@common/hooks';
import { AppointmentManagementItem, IAppointmentManagementGroupingModel, IAppointmentManagementItem } from '@common/models';
import { VisitService } from "@common/services";
import { anyPromiseTrackerTagsInProgress, ordinal } from '@common/utils';
import { normalizePhone } from '@common/utils/phoneUtils';
import { CancelReservationModal, ConfirmReservationModal } from '@customer/components/modals';
import { AppointmentsStore } from '@customer/stores';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Image, ListGroup, Row, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

interface IAppointmentGroupProps {
    store: AppointmentsStore;
    group: IAppointmentManagementGroupingModel;
    groupId: string;
}
interface IAppCannotRescheduleMessageProps {
    hospitalPhone: string;
    extraMessage?: string;
}

const AppCannotRescheduleMessage = ({ hospitalPhone, extraMessage }: IAppCannotRescheduleMessageProps) => (
    <p className="text-center mt-3 w-100">
        This appointment cannot be rescheduled online. Please call the hospital at {hospitalPhone} {extraMessage && <span>{extraMessage}</span>}
    </p>
);

export const AppointmentGroup = observer(({ store, group, groupId }: IAppointmentGroupProps) => {
    const { configuration } = useApp();
    const [resendEmail, setResendEmail] = useState<boolean>(false);
    const [rescheduleItem, setRescheduleItem] = useState<AppointmentManagementItem | undefined>(undefined);
    const navigate = useNavigate();
    const clinicDate = new Date(group.clinicDate);
    const mounted = useRef(true);
    const [showCancelReservation, setShowCancelReservation] = useState<boolean>(false);
    const [showConfirmReservation, setShowConfirmReservation] = useState<boolean>(false);
    const friendlyClinicDate = useMemo(() => {
        return `${DateTime.fromJSDate(group.clinicDate).toFormat("EEEE, MMMM")} ${ordinal.getNumberOrdinal(group.clinicDate.getDate())}`;

    }, [group.clinicDate]);

    const cancellationBody = useMemo(() => {
        if (!store.isReschedule && !store.isRebook) {
            return `Are you sure you want to cancel ${store.petNames}'s appointment(s) on ${friendlyClinicDate}? You will need to book a new appointment(s) for ${store.petNames} if they need to be seen.`;
        } else {
            return `Are you sure? This will Cancel your appointment(s) for ${store.petNames} and allow you to rebook.`;
        }
    }, [friendlyClinicDate, store.isRebook, store.isReschedule, store.petNames]);
   
    const confirmationBody = useMemo(() => {
        return _.isEmpty(group.hospitalName) 
            ? `Your pet's appointment(s) on ${friendlyClinicDate} has been confirmed. We look forward to seeing you. To help us prepare for your visit, please complete your Pet's Intake Form prior to your appointment. Forms are available 48 hours prior to your visit.` 
            :`Your pet's appointment(s) on ${friendlyClinicDate} with Vetco Total Care has been confirmed. We look forward to seeing you.` ;
    }, [friendlyClinicDate, group.hospitalName]);
    //#region Events

    const onConfirmGroupClick = () => {
        store.confirmAppointmentGroup(group)
            .then(() => {
                setShowConfirmReservation(true);
            });
    }

    const onConfirmReservation = () => {
        store.confirm();
        setShowConfirmReservation(false);
    }

    const onCloseModal = () => {
        store.closeCancellation();
        setShowCancelReservation(false);
    }

    const onCancelItemClick = (item: IAppointmentManagementItem, selectedGroupId: string) => {
        setShowCancelReservation(((group.canCancel || group.canReschedule) && groupId === selectedGroupId));
        store.showCancellation([item], false, false);
    }

    const onCancelGroupClick = (selectedGroupId: string) => {
        setShowCancelReservation(((group.canCancel || group.canReschedule) && groupId === selectedGroupId));
        store.showCancellation([...group.items], false, false);
    }

    const onRescheduleGroupClick = (rebook: boolean, selectedGroupId: string) => {
        if (!group.rescheduleViaVOB) {
            if (!rebook) {
                store.showCancellation([...group.items], true, rebook);
            } else {
                // Map returns Array<string | undefined>
                // Compact strips out all falsey values including undefined and ""
                const ids = _.compact(_.map(group.items, 'reservationPublicId'));
                store.cancelAppointmentGroup(ids, true, rebook).then(() => {
                    if (mounted) {
                        store.getAppointmentAndVisitData().then(() => {
                            document.getElementById(groupId)?.click();
                        }).catch(() => {
                            if (mounted)
                                navigate("/verification");
                        });
                    }
                });
            }
        } else {
            setRescheduleItem(undefined);
            document.getElementById(groupId)?.click();
        }
    }

    //#endregion

    useEffect(() => {
        if (!mounted)
            return; //don't do anything
        return () => {
            mounted.current = false;
        };
    }, [mounted]);

    const resendVisitConfirmationEmail = async (items: IAppointmentManagementItem[]) => {
        if (items.length && items.some(i => i.visitPublicId !== undefined)) {
            const visitIds: string[] = [];
            items.forEach(item => {
                if (item.visitPublicId !== undefined) visitIds.push(item.visitPublicId);
            });
            await store.resendVisitCompletionEmail(visitIds);
            setResendEmail(false);
        }
    }

    const shouldSendReservationIdToVOB = (status: string): boolean => {
        return status !== 'Cancelled' && status !== 'Missed'
    };

    return (
        <>
            <Card className="mt-1 mb-4">
                <Card.Body>
                    <Row className="d-flex ">
                        <Col xs={6} sm={6} className='ps-l-0p375'>
                            <span className={
                                classNames({
                                    'text-success': group.status == 'Confirmed' || group.status == 'Completed',
                                    'text-danger': group.status == 'Missed' || group.status == 'Cancelled' || group.status == 'Clinic Cancelled'
                                },
                                    'fs-6')}>
                                {group.status}
                            </span>
                            <p className="fw-bold text-blue">{`${DateTime.fromJSDate(clinicDate).toFormat("EEEE, MMMM")} ${ordinal.getNumberOrdinal(clinicDate.getDate())}, ${clinicDate.getFullYear()}`}</p>
                        </Col>
                        <Col xs={6} sm={6} className='ps-r-0p375'>
                            <div className=" d-flex align-items-center ">
                                <FontAwesomeIcon icon={faMapMarkerAlt} size={'sm'} className='mx-2' />
                                <div className={`d-inline-block fw-normal-bold text-uppercase appt-type-pill font-size-10 round ${_.isEmpty(group.hospitalName) ? 'appt-type-green' : 'appt-type-blue'}`} >
                                    {_.isEmpty(group.hospitalName) ? "Vaccination Clinic" : "Hospital"}
                                </div>
                            </div>
                            <div className={"ms-custom-1p5rem sm-type mt-1 me-lg-3  justify-content-end"}>
                                <p className="fw-bold font-size-12">At {group.partnerName}</p>
                                <p className="text-decoration-underline d-flex  blue-label font-size-12 fw-normal">{group.address}</p>
                                <p className="text-decoration-underline blue-label font-size-12 fw-normal">{group.city}, {group.state} {group.zip}</p>
                            </div>

                        </Col>
                    </Row>
                    <ListGroup variant="flush" className="pb-1">
                        <ListGroup.Item className="px-2">
                            <Row>
                                <Col xs={12}>
                                    <Table>
                                        <thead>
                                            <tr key={"header"} className="appt-tbl-head">
                                                <th></th>
                                                <th></th>
                                                {group.canCancel ? <th></th> : null}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                group.items.map((item) => (
                                                    <tr key={`${item.reservationPublicId}_${item.visitPublicId}_${item.itemTime}`} className="appt-tbl-row font-weight-bold">
                                                        <td className="text-blue">
                                                            {item.species === "Canine"
                                                                ? <Image src={dogIcon} height={26} width={24} />
                                                                : item.species === "Feline"
                                                                    ? <Image src={catIcon} height={26} width={24} />
                                                                    : null} {item.petName}
                                                        </td>
                                                        <td className="text-blue">at <b className="primary">{item.itemTime}</b></td>
                                                        {group.canCancel ?
                                                            <td>
                                                                <a className="primary clickable text-danger" onClick={() => { onCancelItemClick(item, groupId) }}>Cancel</a>
                                                            </td>
                                                            : null}
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        </ListGroup.Item>
                    </ListGroup>

                    <div className="d-grid gap-2 mb-2">
                        {group.canConfirm ?
                            <Button variant="primary" className="text-uppercase btn-customer" type="button" onClick={() => { onConfirmGroupClick() }}>Confirm</Button>
                            : null}
                        {group.canSendVisitCompletion ?
                            <Button variant="primary" className="text-uppercase btn-customer" onClick={() => setResendEmail(true)}>Resend Your Post Visit Summary</Button>
                            : null}
                        {group.canRebook ? (
                            group.isAppointmentTypeOnlineBooking ? (
                                <Button variant="primary" className="text-uppercase btn-customer" onClick={() => { onRescheduleGroupClick(true, groupId) }}>Rebook Appointment</Button>
                            ) : (
                                <AppCannotRescheduleMessage hospitalPhone={normalizePhone(group.hospitalPhone)} extraMessage="to book a new appointment." />
                            )
                        ) : null}
                    </div>

                    <div className={classNames("d-flex gap-2", { 'flex-wrap': (group.isHospitalReservation && !configuration.vobRescheduleEnabled) })}>
                        {group.canCancel ?
                            <Button variant="light" className="text-uppercase w-100 flex-fill btn-customer" onClick={() => { onCancelGroupClick(groupId) }}>Cancel</Button>
                            : null}
                        {group.canReschedule && (!group.isHospitalReservation || (configuration.vobRescheduleEnabled && group.isHospitalReservation && group.isAppointmentTypeOnlineBooking)) &&
                            <Button variant="light" className="text-uppercase w-100 flex-fill btn-customer" onClick={() => onRescheduleGroupClick(false, groupId)}>Reschedule</Button>}
                    </div>
                    {group.canReschedule && group.isHospitalReservation && (!configuration.vobRescheduleEnabled || !group.isAppointmentTypeOnlineBooking) &&
                        <AppCannotRescheduleMessage hospitalPhone={normalizePhone(group.hospitalPhone)} extraMessage='if you need to make any changes.' />}
                </Card.Body>
                {
                    group.canViewIntake ?
                        <Card.Footer>
                            <div className="sm-type mb-2">
                                <p>
                                    <b>
                                        Help us get ready for you pet&apos;s appointment
                                    </b>
                                </p>
                                <p>
                                    Please review or complete your Pet&apos;s Intake Form prior to your visit. This information will help our team provide the best visit tailored to your pet&apos;s needs.
                                </p>
                            </div>
                            <div className="d-flex gap-2 mb-2">
                                <Button variant="primary" className="text-uppercase flex-fill btn-customer" onClick={() => { window.open(`/customer/intake/${group.intakePublicId}?linksource=customerapp`) }}>Complete Form</Button>
                            </div>
                        </Card.Footer>
                        : null
                }
            </Card>
            {
                group.rescheduleViaVOB ?
                    <>
                        <form
                            action={group.vobRescheduleUrl}
                            name={'rebook' + group.clinicId}
                            id={'rebook' + group.clinicId}
                            method="POST"
                            style={{ display: "none" }}
                            target="_blank">
                            <input type="text" readOnly hidden={true} name="firstname" value={group.firstName} />
                            <input type="text" readOnly hidden={true} name="lastname" value={group.lastName} />
                            <input type="text" readOnly hidden={true} name="phone" value={group.phone} />
                            <input type="text" readOnly hidden={true} name="email" value={group.email} />
                            <input type="text" readOnly hidden={true} name="isreturningcustomer" value={group.wasPriorCustomer.toString()} />
                            <input type="text" readOnly hidden={true} name="store_number" value={group.storeCode} />
                            <input type="text" readOnly hidden={true} name="zipcode" value={group.zip} />
                            <input type="text" readOnly hidden={true} name="appointmentTypeId" value={group.publicAppointmentTypeId} />
                            <input type="text" readOnly hidden={true} name="hospitalName" value={group.isHospitalReservation ? group.hospitalName : undefined} />
                            <input type="text" readOnly hidden={true} name="hospitalPhone" value={group.isHospitalReservation ? group.hospitalPhone : undefined} />
                            <input type="text" readOnly hidden={true} name="address" value={group.address} />
                            <input type="text" readOnly hidden={true} name="city" value={group.city} />
                            <input type="text" readOnly hidden={true} name="state" value={group.state} />
                            <input type="text" readOnly hidden={true} name="locationType" value={group.isHospitalReservation ? 'Hospital' : 'VaccinationClinic'} />
                            {_.isUndefined(rescheduleItem) ?
                                group.items.map((item) => (
                                    <div key={item.reservationPublicId}>
                                        <input type="text" readOnly hidden={true} name="petname" value={item.petName} />
                                        <input type="text" readOnly hidden={true} name="petspecies" value={item.species} />
                                        <input type="text" readOnly hidden={true} name="reschedulingReservationId" value={shouldSendReservationIdToVOB(group.status) ? item.reservationPublicId : '[]'} />
                                        <input type="text" readOnly hidden={true} name="reschedulingReservationTime" value={item.itemDateTime} />
                                    </div>
                                ))
                                :
                                <>
                                    <input type="text" readOnly hidden={true} name="petname" value={rescheduleItem.petName} />
                                    <input type="text" readOnly hidden={true} name="petspecies" value={rescheduleItem.species} />
                                    <input type="text" readOnly hidden={true} name="reschedulingReservationId" value={shouldSendReservationIdToVOB(group.status) ? rescheduleItem.reservationPublicId : '[]'} />
                                    <input type="text" readOnly hidden={true} name="reschedulingReservationTime" value={rescheduleItem.itemDateTime} />
                                </>
                            }

                            <button type="submit" id={groupId}>
                                Re-Book Appointment
                            </button>
                        </form>
                    </>
                    : <></>
            }

            {
                group.canConfirm &&
                <ConfirmReservationModal
                    visible={showConfirmReservation}
                    bodyText={confirmationBody}
                    onConfirm={onConfirmReservation} />
            }
            {
                showCancelReservation &&
                <CancelReservationModal
                    visible
                    bodyText={cancellationBody}
                    onConfirm={() => {
                        store.cancelReservationConfirm();

                        if (!group.rescheduleViaVOB)
                            document.getElementById(groupId)?.click();
                        setShowCancelReservation(false);
                    }}
                    onCancel={onCloseModal} />
            }

            <YesNoConfirmModal
                visible={resendEmail}
                busy={anyPromiseTrackerTagsInProgress([VisitService.tags.resendVisitCompletionEmail], 500)}
                title={'Resend Your Post Visit Summary'}
                description={`Are you sure you want to resend your Post Visit Summary documents for ${group.items.map(i => i.petName).join(', ')}.`}
                onConfirm={() => resendVisitConfirmationEmail(group.items)}
                onCancel={() => setResendEmail(false)} />
        </>
    )
});
